// Moved from `src/hooks/query/useQueryGamifyTrialExtension.ts` (now a
// delegate) at D2 batch 2, ARCFE-973.

import { useQuery } from '@tanstack/react-query';
import {
  GET_GAMIFY_TRIAL_EXTENSION,
  type GamifyTrialExtensionResponse,
} from '../../contracts';
import { getGamifyTrialExtension } from '../api/trials';
import { getCommerceDataConfig } from '../config';

export type UseQueryGamifyTrialExtensionProps = {
  /** `Boolean(useQueryUser().data)`, resolved app-side — see the products hook. */
  hasUser: boolean;
  enabled?: boolean;
  /** Read-time transform; the cache keeps the real response. */
  select?: (data: GamifyTrialExtensionResponse) => GamifyTrialExtensionResponse;
};

/**
 * GET /user/gamify-trial-extension.
 *
 * `staleTime` and `refetchOnMount: 'always'` are kept exactly. They look
 * contradictory and are not: the 5-minute stale window suppresses refetches
 * from other mounts, while `'always'` forces one for the component that
 * actually renders the extension offer, so the offer is never shown from a
 * stale cache.
 */
export function useQueryGamifyTrialExtension({
  hasUser,
  enabled = true,
  select,
}: UseQueryGamifyTrialExtensionProps) {
  const { useAuthToken } = getCommerceDataConfig();
  const token = useAuthToken();

  return useQuery<GamifyTrialExtensionResponse>({
    queryKey: [GET_GAMIFY_TRIAL_EXTENSION],
    queryFn: async () => {
      const { data } = await getGamifyTrialExtension();
      return data;
    },
    enabled: enabled && !!token && hasUser,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: 'always',
    select,
  });
}

/**
 * Invalidates THIS query only.
 *
 * The legacy `invalidateGamifyTrialExtension` also awaited
 * `invalidateCurrentUserBillingInfo()`, and the two ran concurrently under a
 * single `Promise.all`. Billing is app-side until the D3 transfer and `/data`
 * may not import a foreign `/data`, so the delegate keeps the `Promise.all`
 * and passes this in as one of its two members — concurrency and the awaited
 * completion both preserved, rather than becoming a sequential pair.
 */
export const invalidateGamifyTrialExtension = async (): Promise<void> => {
  const { queryClient } = getCommerceDataConfig();
  await queryClient.invalidateQueries({ queryKey: [GET_GAMIFY_TRIAL_EXTENSION] });
};
